import {
	VIEWBY_ENUMS,
	CATMANVIEW,
	PERSONAVIEW,
	WMT_ATTR,
	CUSTOM_ATTR,
	WMT_ATTR_LEVEL,
	CUSTOM_ATTR_LEVEL,
} from '../../constants/constant';
import { tableMappingAPI } from '../../components/Table/utils';
import { getPastWMTWeekDateRange } from '../../helper/date/walmart-week';

// This is the Main Query builder method
// It combines filters & schema, forms the GraphQL Query

export const getQuery = (params) => {
	const {
		id,
		name,
		upcs,
		items,
		rhids,
		viewBy,
		channel,
		metrics,
		endDate,
		startDate,
		weekFilter,
		breakdownBy,
		sortOrder,
		sortMetric,
		timePeriods,
		rootParam,
		userDetails,
		selectedView,
		viewByType,
		breakDownByType,
		type,
		standardName,
	} = params;

	const breakDownByLevel =
		breakDownByType === WMT_ATTR ? WMT_ATTR_LEVEL : CUSTOM_ATTR_LEVEL;

	// Filters
	const { filter, additionalFilter } = getFilters({
		id,
		name,
		upcs,
		items,
		rhids,
		viewBy,
		channel,
		endDate,
		startDate,
		weekFilter,
		breakdownBy,
		sortOrder,
		sortMetric,
		timePeriods,
		rootParam,
		userDetails,
		selectedView,
		viewByType,
		breakDownByType,
		breakDownByLevel,
		type,
		standardName,
	});

	// Schema
	let schema = `
		id
        name
		type
		customAttrLevel
		wmtAttrLevel
		roots {
		    type
			id
			customAttrLevel
			wmtAttrLevel
			name	
		}
		upcNbr
		catalogItemId
		primeItemNbr
		allLinkNbr
		${buildSchema(channel, metrics)}
    `;

	// Final Query using Filters and Schema
	const query = `{
		tableRows(
			filter: ${filter}
			additionalFilter: ${additionalFilter}
		) {
			${schema}
		}
	}`;

	return query;
};

// Constants for Date filter type
const durationType = {
	'-1': `CUSTOM`,
	0: `WTD`,
	1: `LW`,
	2: `L2W`,
	4: `L4W`,
	13: `L13W`,
	26: `L26W`,
	52: `L52W`,
};

// Util methods
export const getFilters = ({
	id,
	name,
	upcs,
	items,
	rhids,
	viewBy,
	channel,
	endDate,
	startDate,
	weekFilter,
	breakdownBy,
	sortOrder,
	sortMetric,
	timePeriods,
	rootParam,
	selectedView,
	viewByType,
	breakDownByType,
	breakDownByLevel,
	type,
	standardName,
}) => {
	let filter = ``;
	filter += `
	{
		durations: [
			{
                startDate: "${startDate}",
                endDate: "${endDate}",
				type: ${durationType[weekFilter]}
            },
	`;

	// Check if time periods are requested
	const isTimePeriodsPresent = timePeriods?.length > 0;

	// If yes, Then we should add them to the query schema
	if (isTimePeriodsPresent) {
		// Iterate over time periods
		timePeriods?.map((item = item) => {
			filter += `
			{
				startDate: ${JSON.stringify(
					getPastWMTWeekDateRange(item.shortValue, 'YYYY-MM-DD').startDate
				)},
				endDate: ${JSON.stringify(
					getPastWMTWeekDateRange(item.shortValue, 'YYYY-MM-DD').endDate
				)},
				type: ${item.value}
			},
			`;
		});
		// Close the array
		filter += `
				]
			`;
	} else {
		// Close the array
		filter += `
					]
				`;
	}

	const isUPCPresent = upcs?.length > 0;
	const isItemPresent = items?.length > 0;
	// If we have UPCs present the filter should contains only the UPC filter, not rhids
	if (isUPCPresent) {
		filter += `upcs: ${JSON.stringify(upcs)}`;
	} else if (isItemPresent) {
		filter += `items: ${JSON.stringify(items)}`;
	} else {
		// Otherwise append rhids
		filter += rhids?.length > 0 ? `rhIds: ${JSON.stringify(rhids)}` : ``;
	}

	filter += `
	 	channel: ${channel.toLocaleUpperCase()}
	}`;

	const additionalFilter = getAdditionalFilter({
		id,
		name,
		viewBy,
		breakdownBy,
		sortOrder,
		sortMetric,
		weekFilter,
		rootParam,
		selectedView,
		viewByType,
		breakDownByType,
		breakDownByLevel,
		type,
		standardName,
	});

	return {
		filter,
		additionalFilter,
	};
};

export const getViewByLevelStr = (type, viewByType, viewBy) => {
	if (type) {
		if (type === CUSTOM_ATTR) {
			return viewBy ? `${CUSTOM_ATTR_LEVEL}: "${viewBy}"` : ``;
		} else {
			return viewBy ? `${WMT_ATTR_LEVEL}: ${viewBy}` : ``;
		}
	} else if (viewByType) {
		if (viewByType === CUSTOM_ATTR) {
			return viewBy ? `${CUSTOM_ATTR_LEVEL}: "${viewBy}"` : ``;
		} else {
			return viewBy ? `${WMT_ATTR_LEVEL}: ${viewBy}` : ``;
		}
	}
};

export const getViewByTypeStr = (type, viewByType) => {
	if (type) {
		if (type === CUSTOM_ATTR) {
			return `type: ${CUSTOM_ATTR}`;
		} else {
			return `type: ${WMT_ATTR}`;
		}
	} else if (viewByType) {
		if (viewByType === CUSTOM_ATTR) {
			return `type: ${CUSTOM_ATTR}`;
		} else {
			return `type: ${WMT_ATTR}`;
		}
	}
};

export const getBreakdownByLevelStr = (
	breakDownByType,
	breakDownByLevel,
	breakdownBy
) => {
	if (breakDownByType === CUSTOM_ATTR) {
		return `${breakDownByLevel}: "${breakdownBy}"`;
	} else {
		return `${breakDownByLevel}: ${breakdownBy}`;
	}
};

// This adds fields for additional filters such as view, breakdown
export const getAdditionalFilter = ({
	id,
	name,
	viewBy,
	breakdownBy,
	sortOrder,
	sortMetric,
	weekFilter,
	rootParam,
	selectedView,
	breakDownByType,
	viewByType,
	breakDownByLevel,
	type,
	standardName,
}) => {
	let filterStr = ``;
	const isFilterPresent = id || viewBy || breakdownBy;
	if (!isFilterPresent) {
		return filterStr;
	}

	filterStr += isFilterPresent ? `{` : ``;
	if (viewBy || id) {
		let rootParamsStr;
		// Converting from Array to Object Compatible with GraphQL Query
		if (rootParam !== null && rootParam.length > 0) {
			// Building roots String object for GraphQL
			rootParamsStr = `
			[`;

			for (let i = 0; i < rootParam.length; i++) {
				const rootViewByLevel =
					rootParam[i]?.type === CUSTOM_ATTR
						? `"${rootParam[i]?.customAttrLevel}"`
						: `${rootParam[i]?.wmtAttrLevel}`;
				const viewByLevelStr =
					rootParam[i]?.type === CUSTOM_ATTR
						? CUSTOM_ATTR_LEVEL
						: WMT_ATTR_LEVEL;
				rootParamsStr += `
				{
					id: "${rootParam[i]?.id}",
					${viewByLevelStr}: ${rootViewByLevel},
					name: "${rootParam[i]?.name}",
					type: ${rootParam[i]?.type}
				}
				`;
			}

			rootParamsStr += `
			]`;
		}

		const rootObj =
			rootParam !== null && rootParam.length > 0
				? `roots: ${rootParamsStr}`
				: ``;
		const levelStr = getViewByLevelStr(type, viewByType, viewBy);
		const idStr = id ? `id: "${id}"` : ``;
		const nameStr = name ? `name: "${standardName}"` : ``;
		const viewByTypeStr = getViewByTypeStr(type, viewByType);
		filterStr += `
			view: {
				${levelStr},
				${idStr},
				${nameStr},
				${rootObj},
				${viewByTypeStr}
			}`;
	}

	const breakdownByLevelStr = getBreakdownByLevelStr(
		breakDownByType,
		breakDownByLevel,
		breakdownBy
	);

	// Add the BreakdownBy filter
	if (includeBreakdownBy(viewBy, breakdownBy)) {
		filterStr += `
			breakdown: {
				${breakdownByLevelStr}
				type: ${breakDownByType}
			}`;
	}

	if (sortMetric) {
		filterStr += `
		sortBy: {
			order: ${sortOrder}, 
			metric: ${sortMetric}, 
			durationType: ${durationType[weekFilter]}
		}
		persona: {
			personaView: ${
				selectedView === CATMANVIEW
					? PERSONAVIEW?.CATMAN
					: PERSONAVIEW?.SUPPLIER
			},
		}`;
	}

	filterStr += isFilterPresent ? `}` : ``;

	return filterStr;
};

export const includeBreakdownBy = (viewBy, breakdownBy) => {
	let include = false;
	if (breakdownBy) {
		// set to true if breakdown By is available
		include = true;
		// case 1. if ViewBy value is equals to BreakdownBy, do not pass BreakdownBy value in the Query
		if (viewBy === breakdownBy) {
			include = false;
		}
		// case 2. if ViewBy is SUBCATEGORY and BreakdownBy is CATEGORY do not pass BreakdownBy value in the Query
		if (
			viewBy === VIEWBY_ENUMS.SUBCATEGORY &&
			breakdownBy === VIEWBY_ENUMS.CATEGORY
		) {
			include = false;
		}
		// case 3. if ViewBy is CATEGORY OR SUBCATEGORY and BreakdownBy is DEPARTMENT do not pass BreakdownBy value in the Query
		if (
			(viewBy === VIEWBY_ENUMS.CATEGORY ||
				viewBy === VIEWBY_ENUMS.SUBCATEGORY) &&
			breakdownBy === VIEWBY_ENUMS.DEPARTMENT
		) {
			include = false;
		}
		// case 4. if ViewBy is STORE and BreakdownBy is STATE do not pass BreakdownBy value in the Query
		if (viewBy === VIEWBY_ENUMS.STORE && (
			breakdownBy === VIEWBY_ENUMS.STATE || 
			breakdownBy === VIEWBY_ENUMS.MARKET ||
			breakdownBy === VIEWBY_ENUMS.SUB_DIVISION ||
			breakdownBy === VIEWBY_ENUMS.REGION
		)) {
			include = false;
		}
		// case 5. if ViewBy is FINELINE and Breakdown by is DEPARTMENT or CATEGORY or SUBCATEGORY
		if(viewBy === VIEWBY_ENUMS.FINELINE && (
			breakdownBy === VIEWBY_ENUMS.DEPARTMENT ||
			breakdownBy === VIEWBY_ENUMS.CATEGORY ||
			breakdownBy === VIEWBY_ENUMS.SUBCATEGORY
		)) {
			include = false
		}
	}
	return include;
};

// This builds the JSON type Schema for the GraphQL query using the User selected metrics
export const buildSchema = (channel, selectedMetrics) => {
	// defaultMetrics contains all metrics
	const defaultMetrics = tableMappingAPI(channel);
	// User selected metrics
	let selectedMetricsNames = Object.keys(selectedMetrics);

	// If there is No selectedMetrics, pick all the default metrics
	if (selectedMetricsNames?.length === 0) {
		selectedMetricsNames = Object.keys(defaultMetrics);
	}

	const userSelectedMetrics = {};

	// This will create nested object with user selected metrics and segments
	if (selectedMetricsNames?.length > 0) {
		for (const metricName in defaultMetrics) {
			if (selectedMetricsNames.includes(metricName)) {
				let selectedSegmentNames = selectedMetrics[metricName] || [];
				const defaultSegmentNames = Object.keys(defaultMetrics[metricName]);

				// If there is No selectedSegment, pick all the default segments
				if (selectedSegmentNames?.length === 0) {
					selectedSegmentNames = defaultSegmentNames;
				}

				selectedSegmentNames?.map((selectedSegmentName) => {
					if (defaultSegmentNames?.includes(selectedSegmentName)) {
						userSelectedMetrics[[metricName]] = {
							...userSelectedMetrics[[metricName]],
							[selectedSegmentName]:
								defaultMetrics[metricName][selectedSegmentName],
						};
					}
				});
			}
		}
	}

	/**
	 * Below iteration builds the string which looks like the following
	 * `
	 * 	netSales {
	 * 		ty_ns : ty
			percentYoY_ns: percentYoY
			absoluteYoY_ns: absoluteYoY
	 * 	}
	 * 	
	 * `
	 */
	let schema = ``;
	if (Object.keys(userSelectedMetrics).length > 0) {
		// metricName : sales ...
		for (const metricName in userSelectedMetrics) {
			const segments = userSelectedMetrics[metricName];
			for (const segment in segments) {
				schema += `
					${segment} {`;
				// attributes : ty, percentYoY ...
				const attributes = segments[segment];
				for (const field in attributes) {
					// fieldAlias : ty_ns, percentYoY_ns ...
					const fieldAlias = attributes[field];
					schema += `
						${fieldAlias} : ${field}`;
				}
				schema += `
					}`;
			}
		}
	}

	return schema;
};
